/**
 * Simplified Cache Buster for SAPUI5 Applications
 * Focuses on version-based cache invalidation without service workers
 */
export class SimpleCacheBuster {
	private static readonly STORAGE_KEY = "app_version";
	private static readonly BUILD_INFO_KEY = "build_info";

	/**
	 * Initialize simplified cache buster
	 */
	public static async init(): Promise<void> {
		console.log("[SimpleCacheBuster] Initializing...");

		try {
			const currentVersion = SimpleCacheBuster.getCurrentVersion();
			const storedVersion = SimpleCacheBuster.getStoredVersion();

			console.log(`[SimpleCacheBuster] Current version: ${currentVersion}`);
			console.log(`[SimpleCacheBuster] Stored version: ${storedVersion}`);

			if (currentVersion && storedVersion && currentVersion !== storedVersion) {
				console.log(
					`[SimpleCacheBuster] Version changed: ${storedVersion} -> ${currentVersion}`,
				);
				await SimpleCacheBuster.clearBrowserCache();
				SimpleCacheBuster.setStoredVersion(currentVersion);
				console.log("[SimpleCacheBuster] Cache cleared due to version change");
			} else if (currentVersion) {
				SimpleCacheBuster.setStoredVersion(currentVersion);
			}

			// Store build information
			SimpleCacheBuster.storeBuildInfo();

			console.log("[SimpleCacheBuster] Initialization completed");
		} catch (error) {
			console.error("[SimpleCacheBuster] Initialization failed:", error);
		}
	}

	/**
	 * Get current version from multiple sources
	 */
	public static getCurrentVersion(): string | null {
		try {
			// 1. Check build timestamp cache-buster meta tag
			const cacheBusterVersion = document
				.querySelector('meta[name="cache-buster"]')
				?.getAttribute("content");
			if (cacheBusterVersion) return cacheBusterVersion;

			// 2. Check meta tag
			const metaVersion = document
				.querySelector('meta[name="app-version"]')
				?.getAttribute("content");
			if (metaVersion) return metaVersion;

			// 3. Check URL parameter (useful for testing)
			const urlParams = new URLSearchParams(window.location.search);
			const urlVersion = urlParams.get("version");
			if (urlVersion) return urlVersion;

			// 4. Generate version based on current timestamp (development)
			const now = new Date();
			const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD
			const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, ""); // HHMMSS

			return `dev-${dateStr}-${timeStr}`;
		} catch (error) {
			console.error("[SimpleCacheBuster] Error getting version:", error);
			return "1.0.0";
		}
	}

	/**
	 * Store build information for debugging
	 */
	private static storeBuildInfo(): void {
		try {
			const buildInfo = {
				timestamp: new Date().toISOString(),
				userAgent: navigator.userAgent,
				url: window.location.href,
				version: SimpleCacheBuster.getCurrentVersion(),
			};

			localStorage.setItem(
				SimpleCacheBuster.BUILD_INFO_KEY,
				JSON.stringify(buildInfo),
			);
		} catch (error) {
			console.warn("[SimpleCacheBuster] Could not store build info:", error);
		}
	}

	/**
	 * Clear browser cache using available APIs
	 */
	public static async clearBrowserCache(): Promise<void> {
		console.log("[SimpleCacheBuster] Clearing browser cache...");

		try {
			let clearedItems = 0;

			// 1. Clear Cache API if available
			if ("caches" in window) {
				const cacheNames = await caches.keys();
				if (cacheNames.length > 0) {
					console.log(
						`[SimpleCacheBuster] Clearing ${cacheNames.length} cache(s)`,
					);
					await Promise.all(
						cacheNames.map((name) => {
							console.log(`[SimpleCacheBuster] Clearing cache: ${name}`);
							return caches.delete(name);
						}),
					);
					clearedItems += cacheNames.length;
				}
			}

			// 2. Clear localStorage (preserve version and essential data)
			const keysToPreserve = [
				SimpleCacheBuster.STORAGE_KEY,
				SimpleCacheBuster.BUILD_INFO_KEY,
				"sap-ui-config", // Preserve UI5 configuration
				"sap-ui-theme", // Preserve theme settings
			];

			const allKeys = Object.keys(localStorage);
			let localStorageCleared = 0;

			allKeys.forEach((key) => {
				if (!keysToPreserve.some((preserve) => key.includes(preserve))) {
					localStorage.removeItem(key);
					localStorageCleared++;
				}
			});

			if (localStorageCleared > 0) {
				console.log(
					`[SimpleCacheBuster] Cleared ${localStorageCleared} localStorage items`,
				);
			}

			// 3. Clear sessionStorage completely
			const sessionKeys = Object.keys(sessionStorage);
			if (sessionKeys.length > 0) {
				sessionStorage.clear();
				console.log(
					`[SimpleCacheBuster] Cleared ${sessionKeys.length} sessionStorage items`,
				);
			}

			// 4. Clear IndexedDB if UI5 uses it (optional)
			if ("indexedDB" in window) {
				// This is more complex and usually not needed for UI5 apps
				console.log(
					"[SimpleCacheBuster] IndexedDB clearing skipped (not typically needed)",
				);
			}

			console.log(
				`[SimpleCacheBuster] Cache clearing completed. Total items cleared: ${clearedItems + localStorageCleared + sessionKeys.length}`,
			);
		} catch (error) {
			console.error("[SimpleCacheBuster] Error clearing cache:", error);
		}
	}

	/**
	 * Force reload with cache bypass
	 */
	public static forceReload(): void {
		console.log("[SimpleCacheBuster] Force reloading with cache bypass...");
		// Use location.reload(true) if available, fallback to regular reload
		if (typeof (window.location as any).reload === "function") {
			(window.location as any).reload(true); // Force from server
		} else {
			window.location.href = window.location.href + "?cachebust=" + Date.now();
		}
	}

	/**
	 * Get diagnostic information
	 */
	public static getDiagnostics(): any {
		return {
			currentVersion: SimpleCacheBuster.getCurrentVersion(),
			storedVersion: SimpleCacheBuster.getStoredVersion(),
			buildInfo: SimpleCacheBuster.getBuildInfo(),
			cacheAPISupported: "caches" in window,
			serviceWorkerSupported: "serviceWorker" in navigator,
			localStorageSupported: "localStorage" in window,
			sessionStorageSupported: "sessionStorage" in window,
			timestamp: new Date().toISOString(),
		};
	}

	/**
	 * Get build information
	 */
	private static getBuildInfo(): any {
		try {
			const buildInfoStr = localStorage.getItem(
				SimpleCacheBuster.BUILD_INFO_KEY,
			);
			return buildInfoStr ? JSON.parse(buildInfoStr) : null;
		} catch (error) {
			return null;
		}
	}

	private static getStoredVersion(): string | null {
		try {
			return localStorage.getItem(SimpleCacheBuster.STORAGE_KEY);
		} catch (error) {
			return null;
		}
	}

	private static setStoredVersion(version: string): void {
		try {
			localStorage.setItem(SimpleCacheBuster.STORAGE_KEY, version);
		} catch (error) {
			console.warn("[SimpleCacheBuster] Could not store version:", error);
		}
	}
}

export default SimpleCacheBuster;
